import { useMemo, useState, useCallback } from 'react';
import { View, Text, Pressable, Platform, ActivityIndicator } from 'react-native';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { Check } from 'lucide-react-native';
import { WEB_HOVER_TRANSITION } from '../../../shared/constants/animations';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../shared/helpers/imageWebAlt.helper';
import { getCdnUrlExactKey, ASSET_PATHS } from '../../../../constants/assets';
import { useTheme } from '../../../theme/useTheme';
import { useCounterTo } from '../../helpers/useCounterTo.hook';
import { PALETTE } from '../CoinStoreModal/CoinStoreModal.constants';
import { proUpgradeCardStyles } from './ProUpgradeCard.styles';
import {
  PRO_PERKS,
  PRO_MONTHLY_CENTS,
  PRO_YEARLY_MONTHLY_CENTS,
  PRO_YEARLY_LABEL,
  PRO_YEARLY_SAVE_PCT,
} from './ProUpgradeCard.constants';

export interface ProUpgradeCardProps {
  /**
   * True when the user holds the Pro entitlement on ANY platform (planTier === 'PRO').
   * Drives the ACTIVE badge and HIDES the purchase CTA — a store-bought Pro user must never be
   * offered a second, parallel Stripe purchase.
   */
  isPro: boolean;
  /**
   * True only when there is a real, manageable subscription on THIS surface (web: active Stripe;
   * native: the store entitlement). Drives the "Manage subscription" CTA.
   */
  isManageable: boolean;
  onPurchase: (billingInterval: 'month' | 'year') => void;
  onManage: () => void;
  loading: boolean;
}

function formatCents(cents: number): string {
  const dollars = Math.floor(cents / 100);
  const remainder = cents % 100;
  return `${dollars}.${String(remainder).padStart(2, '0')}`;
}

export function ProUpgradeCard({ isPro, isManageable, onPurchase, onManage, loading }: ProUpgradeCardProps) {
  const theme = useTheme();
  const styles = useMemo(() => proUpgradeCardStyles(theme), [theme]);
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('year');

  const targetCents = billingInterval === 'year' ? PRO_YEARLY_MONTHLY_CENTS : PRO_MONTHLY_CENTS;
  const animatedCents = useCounterTo(targetCents, 600);
  const formattedPrice = formatCents(animatedCents);

  const crownUri = getCdnUrlExactKey(ASSET_PATHS.CROWN_ICON);

  const handlePurchase = useCallback(() => {
    onPurchase(billingInterval);
  }, [onPurchase, billingInterval]);

  return (
    <View style={styles.card}>
      <LinearGradient
        colors={[PALETTE.violetLight, PALETTE.violetMid, PALETTE.violetDark]}
        locations={[0, 0.45, 1]}
        style={styles.gradientFill}
      />
      <View style={styles.inner}>

        {/* Header row: crown + plan name + badge */}
        <View style={styles.headerRow}>
          <View style={styles.crownCircle}>
            <Image
              source={{ uri: crownUri }}
              style={styles.crownImg}
              contentFit="contain"
              alt={WEB_IMAGE_ALT_DECORATIVE}
              tintColor={theme.accent}
            />
          </View>
          <Text style={styles.planName}>Zporti Pro</Text>
          {isPro ? (
            <View style={styles.activeBadge}>
              <Check size={9} color="#062417" strokeWidth={3} />
              <Text style={styles.activeBadgeText}>ACTIVE</Text>
            </View>
          ) : (
            <View style={styles.proBadge}>
              <Text style={styles.proBadgeText}>MOST POPULAR</Text>
            </View>
          )}
        </View>

        {/* Billing toggle */}
        <View style={styles.toggleRow}>
          {(['month', 'year'] as const).map((interval) => {
            const isActive = billingInterval === interval;
            return (
              <Pressable
                key={interval}
                accessibilityRole="button"
                accessibilityState={{ selected: isActive }}
                onPress={() => setBillingInterval(interval)}
                style={({ pressed }) => [
                  styles.toggleBtn,
                  isActive ? styles.toggleBtnActive : styles.toggleBtnInactive,
                  Platform.select({ web: { transition: WEB_HOVER_TRANSITION } as object }),
                  pressed && styles.toggleBtnPressed,
                ]}
              >
                <View style={styles.toggleBtnInner}>
                  <Text style={[styles.toggleBtnLabel, isActive ? styles.toggleBtnLabelActive : styles.toggleBtnLabelInactive]}>
                    {interval === 'month' ? 'Monthly' : 'Yearly'}
                  </Text>
                  {interval === 'year' && (
                    <View style={styles.savePill}>
                      <Text style={styles.savePillText}>SAVE {PRO_YEARLY_SAVE_PCT}%</Text>
                    </View>
                  )}
                </View>
              </Pressable>
            );
          })}
        </View>

        {/* Price */}
        <View style={styles.priceBlock}>
          <View style={styles.priceRow}>
            <Text style={styles.priceDollar}>$</Text>
            <Text style={styles.priceAmount}>{formattedPrice}</Text>
            <Text style={styles.pricePer}>/mo</Text>
          </View>
          {billingInterval === 'year' && (
            <Text style={styles.priceSub}>Billed {PRO_YEARLY_LABEL}/yr</Text>
          )}
        </View>

        <View style={styles.divider} />

        {/* Perks */}
        <View style={styles.perkList}>
          {PRO_PERKS.map((perk) => (
            <View key={perk.key} style={styles.perkRow}>
              <View style={styles.perkCheck}>
                <Check size={10} color={theme.accent} strokeWidth={3.5} />
              </View>
              <Text style={styles.perkLabel}>{perk.label}</Text>
            </View>
          ))}
        </View>

        {/* CTA */}
        {isManageable ? (
          <Pressable
            accessibilityRole="button"
            onPress={onManage}
            style={({ pressed }) => [styles.manageBtn, pressed && styles.manageBtnPressed]}
          >
            <Text style={styles.manageBtnText}>Manage subscription</Text>
          </Pressable>
        ) : isPro ? (
          // Pro on another billing rail (e.g. bought in the mobile app, viewing on web). Never offer
          // a second purchase here — route to where the subscription actually lives (onManage follows
          // proProvider: store settings for a RevenueCat sub).
          <Pressable
            accessibilityRole="button"
            onPress={onManage}
            style={({ pressed }) => [styles.manageBtn, pressed && styles.manageBtnPressed]}
          >
            <Text style={styles.manageBtnText}>Manage subscription</Text>
          </Pressable>
        ) : (
          <Pressable
            accessibilityRole="button"
            disabled={loading}
            onPress={handlePurchase}
            style={({ pressed }) => [
              styles.ctaBtn,
              pressed && styles.ctaBtnPressed,
              loading && styles.ctaBtnLoading,
            ]}
          >
            <LinearGradient
              colors={[PALETTE.buyGreen, PALETTE.buyGreenDark]}
              style={styles.ctaBtnGradient}
            />
            {loading
              ? <ActivityIndicator size="small" color="#fff" />
              : <Text style={styles.ctaBtnText}>Unlock Pro</Text>
            }
          </Pressable>
        )}

        <Text style={styles.renewalNote}>Renews automatically · Cancel anytime</Text>
      </View>
    </View>
  );
}
